import { Component, ElementRef, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { noop } from 'rxjs';
import { FocusableControlValueAccessor } from '../FocusableControlValueAccessor';

@Component({
  selector: 'opac-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
})
export class CheckboxComponent implements FocusableControlValueAccessor {
  /**
   * Sets boolean checked state
   */
  @Input('checked') set isChecked(value: boolean) {
    this.writeValue(value);
  }
  /**
   * Sets boolean disabled state
   */
  @Input() set disabled(value: boolean) {
    this.setDisabledState(value);
  }
  /**
   * Determines if boolean is invalid.
   */
  @Input() invalid: boolean = false;
  /**
   * Determines hidden input name to work with forms
   */
  @Input() name: string = '';
  /**
   * Sets required state for boolean.
   * This prop won't validate, it is just for accessibility reasons.
   */
  @Input() required: boolean = false;

  /**
   * Emits when element loose focus
   */
  @Output() blur = new EventEmitter<Event>();
  /**
   * Emits when checked property changes value
   */
  @Output() change = new EventEmitter<boolean>();

  @ViewChild('checkboxEl') checkboxEl: ElementRef<HTMLInputElement>;

  checked = false;
  isDisabled = false;

  onChange: (value: boolean) => void = noop;
  onTouch: () => void = noop;

  handleOnTouch(event: Event) {
    this.onTouch();
    this.blur.emit(event);
  }

  toggleCheckbox() {
    if (this.isDisabled) {
      return;
    }

    this.checked = !this.checked;
    this.change.emit(this.checked);
    this.onChange(this.checked);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  focus() {
    this.checkboxEl.nativeElement.focus();
  }

  writeValue(obj: boolean): void {
    this.checked = obj;
  }
}
